import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { decodeToken, useAuthenticatedUser } from "../Auth/AuthContext";
import {
  Alert,
  Button,
  Checkbox,
  Dropdown,
  Form,
  Input,
  Modal,
  Radio,
  Select,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import type { MenuProps } from "antd";
import {
  DeleteOutlined,
  MailOutlined,
  MoreOutlined,
  ReloadOutlined,
  SendOutlined,
} from "@ant-design/icons";
import {
  deleteIdentityMutation,
  inviteUserMutation,
  listIdentitiesOptions,
  resendUserInviteMutation,
} from "../../services/client/@tanstack/react-query.gen";
import { adminLoginAsPartner, listSites } from "../../services/client";
import type {
  IdentityResponse,
  PaginatedResponseSiteReadable,
  PartnerOutput,
  SiteReadable,
} from "../../services/client/types.gen";
import { extractErrorMessage } from "../../util/extractErrorMessage";
import {
  identityTypeColors as typeColors,
  identityTypeLabels as typeLabels,
  identityViewerColor,
  identityViewerLabel,
} from "../../util/identityType";
import { useIsViewer } from "../../hooks/useIsViewer";
import { DisableForViewer } from "../common/DisableForViewer";

const { Text } = Typography;

type IdentityType = IdentityResponse["type"];

// /admin sits outside the AuthenticationGuard, so the client has no token
// configured and the admin token must be attached per request.
const adminAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("admin-token")}`,
});

// The id of the currently-authenticated identity lives under a different
// field depending on the JWT variant (admin_id, user_id, owner_id).
const getIdentityId = (
  token: Record<string, unknown> | null | undefined,
): string | undefined => {
  const id = token?.admin_id ?? token?.user_id ?? token?.owner_id;
  return typeof id === "string" ? id : undefined;
};

type InviteFormValues = {
  type: IdentityType;
  email: string;
  partnerId?: string;
  siteIds?: string[];
  isViewer?: boolean;
};

type IdentitiesBoardProps = {
  /** "admin" manages every partner's identities and can invite admins too;
   * "partner" is scoped to the caller's own partner. */
  variant: "admin" | "partner";
  /** Required for variant "admin", used to resolve partner names and to look
   * up a partner's sites when inviting a site owner. */
  partners?: PartnerOutput[];
};

export const IdentitiesBoard: React.FC<IdentitiesBoardProps> = ({
  variant,
  partners,
}) => {
  const isAdmin = variant === "admin";
  const isViewer = useIsViewer();
  const authenticatedUser = useAuthenticatedUser();
  const currentIdentityId = isAdmin
    ? getIdentityId(
        (() => {
          const adminToken = localStorage.getItem("admin-token");
          return adminToken ? decodeToken(adminToken) : null;
        })(),
      )
    : getIdentityId(authenticatedUser);
  const [inviteModalVisible, setInviteModalVisible] = useState(false);
  const [typeFilter, setTypeFilter] = useState<IdentityType | "all">("all");
  const [search, setSearch] = useState("");
  const [inviteForm] = Form.useForm<InviteFormValues>();
  const selectedType = Form.useWatch("type", inviteForm);
  const selectedPartnerId = Form.useWatch("partnerId", inviteForm);

  const {
    data: identities,
    isPending,
    isFetching,
    error,
    refetch,
  } = useQuery({
    ...listIdentitiesOptions(isAdmin ? { headers: adminAuthHeaders() } : {}),
    retry: false,
  });

  const partnerNameById = useMemo(
    () =>
      Object.fromEntries(
        (partners ?? []).map((partner) => [partner.partner_id, partner.name]),
      ),
    [partners],
  );

  // Admin needs to mint a partner-scoped token via adminLoginAsPartner to list
  // a partner's sites; a partner already has one, so it can query directly.
  const availableSites = useQuery({
    queryKey: isAdmin
      ? ["admin-partner-sites", selectedPartnerId]
      : ["own-sites-for-invite"],
    enabled:
      inviteModalVisible &&
      selectedType === "site_owner" &&
      (!isAdmin || Boolean(selectedPartnerId)),
    retry: false,
    queryFn: async () => {
      const siteHeaders = isAdmin
        ? {
            Authorization: `Bearer ${
              (
                await adminLoginAsPartner({
                  throwOnError: true,
                  headers: {
                    ...adminAuthHeaders(),
                    partner_id: selectedPartnerId ?? "",
                  },
                })
              ).data.access_token
            }`,
          }
        : undefined;

      const sites: SiteReadable[] = [];
      let after: string | undefined = undefined;
      do {
        const page = await listSites({
          throwOnError: true,
          headers: siteHeaders,
          query: { page_size: 100, after },
        });
        const body: PaginatedResponseSiteReadable = page.data;
        sites.push(...body.data);
        after = body.has_next_page ? (body.end_cursor ?? undefined) : undefined;
      } while (after);
      return sites;
    },
  });

  const inviteMutation = useMutation({ ...inviteUserMutation() });
  const isInviting = inviteMutation.isPending;

  const resendInviteMutation = useMutation({ ...resendUserInviteMutation() });
  const isResendingInvite = (identity: IdentityResponse) =>
    resendInviteMutation.isPending &&
    resendInviteMutation.variables?.path.identity_id === identity.id;

  const deleteMutation = useMutation({ ...deleteIdentityMutation() });
  const isDeleting = (identity: IdentityResponse) =>
    deleteMutation.isPending &&
    deleteMutation.variables?.path.identity_id === identity.id;

  const filteredIdentities = useMemo(() => {
    const query = search.trim().toLowerCase();
    return (identities ?? [])
      .filter(
        (identity) => typeFilter === "all" || identity.type === typeFilter,
      )
      .filter((identity) => {
        if (!query) {
          return true;
        }
        return [
          ...identity.client_ids,
          identity.partner_id ?? "",
          partnerNameById[identity.partner_id ?? ""] ?? "",
          ...(identity.site_ids ?? []),
        ]
          .join(" ")
          .toLowerCase()
          .includes(query);
      });
  }, [identities, typeFilter, search, partnerNameById]);

  const closeInviteModal = () => {
    setInviteModalVisible(false);
    inviteForm.resetFields();
  };

  const sendInvite = async (values: InviteFormValues) => {
    try {
      await inviteMutation.mutateAsync({
        body:
          values.type === "admin"
            ? {
                type: "admin",
                email: values.email,
                is_viewer: values.isViewer,
              }
            : values.type === "site_owner"
              ? {
                  type: "site_owner",
                  email: values.email,
                  partner_id: isAdmin ? (values.partnerId ?? "") : undefined,
                  site_ids: values.siteIds ?? [],
                  is_viewer: values.isViewer,
                }
              : {
                  type: "user",
                  email: values.email,
                  partner_id: isAdmin ? (values.partnerId ?? "") : undefined,
                  is_viewer: values.isViewer,
                },
        ...(isAdmin ? { headers: adminAuthHeaders() } : {}),
        throwOnError: true,
      });
    } catch (inviteError) {
      message.error(
        `Failed to send invite: ${extractErrorMessage(inviteError)}`,
      );
      return;
    }
    message.success(`Invitation sent to ${values.email}!`);
    closeInviteModal();
    await refetch();
  };

  const handleResendInvite = async (identity: IdentityResponse) => {
    try {
      await resendInviteMutation.mutateAsync({
        path: { identity_id: identity.id },
        ...(isAdmin ? { headers: adminAuthHeaders() } : {}),
        throwOnError: true,
      });
    } catch (resendError) {
      message.error(
        `Failed to resend invite: ${extractErrorMessage(resendError)}`,
      );
      return;
    }
    message.success("Invitation resent!");
    await refetch();
  };

  const handleDelete = async (identity: IdentityResponse) => {
    try {
      await deleteMutation.mutateAsync({
        path: { identity_id: identity.id },
        ...(isAdmin ? { headers: adminAuthHeaders() } : {}),
        throwOnError: true,
      });
    } catch (deleteError) {
      message.error(
        `Failed to delete identity: ${extractErrorMessage(deleteError)}`,
      );
      return;
    }
    message.success("Identity deleted!");
    await refetch();
  };

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="Failed to load identities"
        description={extractErrorMessage(error)}
      />
    );
  }

  if (isPending) {
    return (
      <div className="flex justify-center py-16">
        <Spin />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
        <div>
          <Text strong className="block text-base">
            {isAdmin ? "All identities" : "Your team"}
          </Text>
          <Text type="secondary">
            {isAdmin
              ? "Every registered identity across all partners. Partner users are scoped to one partner, site owners to an explicit list of sites."
              : "People with access to your account. Users can manage all of your sites; site owners are limited to the sites you choose for them."}
          </Text>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select<IdentityType | "all">
            value={typeFilter}
            onChange={setTypeFilter}
            className="w-36"
            options={[
              { label: "All types", value: "all" },
              ...Object.entries(typeLabels)
                .filter(([value]) => isAdmin || value !== "admin")
                .map(([value, label]) => ({ label, value })),
            ]}
          />
          <Input.Search
            placeholder="Search client, partner or site"
            allowClear
            className="w-64"
            onChange={(event) => setSearch(event.target.value)}
          />
          <Button
            icon={<ReloadOutlined />}
            loading={isFetching}
            onClick={() => refetch()}
          >
            Refresh
          </Button>
          <DisableForViewer>
            <Button
              type="primary"
              icon={<MailOutlined />}
              onClick={() => setInviteModalVisible(true)}
            >
              Invite by email
            </Button>
          </DisableForViewer>
        </div>
      </div>

      <Table<IdentityResponse>
        rowKey="id"
        dataSource={filteredIdentities}
        pagination={{ pageSize: 20, hideOnSinglePage: true }}
        columns={[
          {
            title: "Type",
            dataIndex: "type",
            width: 180,
            render: (type: IdentityType, identity) => (
              <>
                <Tag color={typeColors[type]}>{typeLabels[type]}</Tag>
                {identity.is_viewer && (
                  <Tag color={identityViewerColor}>{identityViewerLabel}</Tag>
                )}
              </>
            ),
          },
          {
            title: "Client IDs",
            dataIndex: "client_ids",
            render: (clientIds: string[]) =>
              clientIds.map((clientId) => (
                <Text key={clientId} code copyable>
                  {clientId}
                </Text>
              )),
          },
          {
            title: "Status",
            dataIndex: "registration_completed",
            width: 140,
            render: (registrationCompleted: boolean | undefined) =>
              registrationCompleted === false ? (
                <Tag color="orange">Invite pending</Tag>
              ) : (
                <Tag color="green">Active</Tag>
              ),
          },
          ...(isAdmin
            ? [
                {
                  title: "Partner",
                  dataIndex: "partner_id",
                  render: (partnerId: string | null) =>
                    partnerId ? (
                      <>
                        <Text>{partnerNameById[partnerId] ?? partnerId}</Text>
                        {partnerNameById[partnerId] && (
                          <Text type="secondary" className="block break-all">
                            {partnerId}
                          </Text>
                        )}
                      </>
                    ) : (
                      <Text type="secondary">—</Text>
                    ),
                },
              ]
            : []),
          {
            title: "Sites",
            dataIndex: "site_ids",
            render: (siteIds: string[] | null) =>
              siteIds?.length ? (
                siteIds.map((siteId) => <Tag key={siteId}>{siteId}</Tag>)
              ) : (
                <Text type="secondary">—</Text>
              ),
          },
          {
            title: "Actions",
            width: 60,
            align: "center",
            render: (_, identity) => {
              const canResend = !identity.registration_completed;
              const canDelete =
                (isAdmin || identity.type === "site_owner") &&
                identity.id !== currentIdentityId;
              if (!canResend && !canDelete) {
                return <Text type="secondary">—</Text>;
              }

              const identityLabel = identity.client_ids.join(", ");

              const items: MenuProps["items"] = [
                ...(canResend
                  ? [
                      {
                        key: "resend",
                        icon: <SendOutlined />,
                        label: "Resend invite",
                        disabled: isViewer,
                        onClick: () => handleResendInvite(identity),
                      },
                    ]
                  : []),
                ...(canDelete
                  ? [
                      {
                        key: "delete",
                        danger: true,
                        icon: <DeleteOutlined />,
                        label: "Delete",
                        disabled: isViewer,
                        onClick: () =>
                          Modal.confirm({
                            title: "Delete this identity?",
                            content: (
                              <>
                                This cannot be undone. You are about to delete{" "}
                                <Text strong>{identityLabel}</Text>.
                              </>
                            ),
                            okText: "Delete",
                            okType: "danger",
                            onOk: () => handleDelete(identity),
                          }),
                      },
                    ]
                  : []),
              ];

              return (
                <Dropdown menu={{ items }} trigger={["click"]}>
                  <Button
                    size="small"
                    icon={<MoreOutlined />}
                    loading={
                      isResendingInvite(identity) || isDeleting(identity)
                    }
                  />
                </Dropdown>
              );
            },
          },
        ]}
      />

      <Modal
        open={inviteModalVisible}
        onCancel={closeInviteModal}
        footer={null}
        title="Invite by email"
      >
        <Text type="secondary" className="mb-4 block">
          The invitee receives an email with a link to set their own password.
          The invite link expires after 7 days.
        </Text>

        <Form<InviteFormValues>
          form={inviteForm}
          layout="vertical"
          initialValues={{ type: "user", isViewer: false }}
          onFinish={sendInvite}
        >
          <Form.Item name="type" label="Type" rules={[{ required: true }]}>
            <Radio.Group
              options={[
                { label: typeLabels.user, value: "user" },
                { label: typeLabels.site_owner, value: "site_owner" },
                ...(isAdmin
                  ? [{ label: typeLabels.admin, value: "admin" }]
                  : []),
              ]}
              optionType="button"
              onChange={() => {
                inviteForm.setFieldValue("partnerId", undefined);
                inviteForm.setFieldValue("siteIds", []);
              }}
            />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Please input the email" },
              { type: "email", message: "Please input a valid email" },
            ]}
          >
            <Input placeholder="partner@example.com" />
          </Form.Item>

          {isAdmin && selectedType !== "admin" && (
            <Form.Item
              name="partnerId"
              label="Partner"
              extra={
                selectedType === "site_owner"
                  ? "Only used to look up sites; the site owner itself is scoped to the selected sites, not the partner."
                  : undefined
              }
              rules={[{ required: true, message: "Please select a partner" }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Select a partner"
                onChange={() => inviteForm.setFieldValue("siteIds", [])}
                options={(partners ?? []).map((partner) => ({
                  label: `${partner.name} (${partner.partner_id})`,
                  value: partner.partner_id,
                }))}
              />
            </Form.Item>
          )}

          {selectedType === "site_owner" && (
            <Form.Item
              name="siteIds"
              label="Sites"
              rules={[
                { required: true, message: "Please select at least one site" },
              ]}
            >
              <Select
                mode="multiple"
                showSearch
                optionFilterProp="label"
                disabled={isAdmin && !selectedPartnerId}
                loading={availableSites.isFetching}
                placeholder={
                  isAdmin && !selectedPartnerId
                    ? "Select a partner first"
                    : "Select sites"
                }
                notFoundContent={
                  availableSites.error
                    ? "Failed to load sites"
                    : availableSites.isFetching
                      ? "Loading sites…"
                      : "No sites found"
                }
                options={(availableSites.data ?? []).map((site) => ({
                  label: `${site.name} (${site.site_id})`,
                  value: site.site_id,
                }))}
              />
            </Form.Item>
          )}

          <Form.Item name="isViewer" valuePropName="checked">
            <Checkbox>
              Invite as read-only viewer
              <Text type="secondary" className="block">
                Viewers can see everything but cannot make any changes.
              </Text>
            </Checkbox>
          </Form.Item>

          <Form.Item className="mb-0">
            <Button
              type="primary"
              htmlType="submit"
              className="w-full"
              loading={isInviting}
            >
              Send invite
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
